import { useLocalization } from "@fluent/react";
import {
  Button,
  ContentCard,
  Spinner,
  formatNumber,
} from "@narmi/design_system";

import MoneyBar from "../MoneyBar/MoneyBar";

import styles from "./ResultSummary.module.scss";

import type { TransactionStats } from "../../hooks/useTransactionStats";

export interface ResultSummaryProps {
  stats: TransactionStats | null;
  isLoading: boolean;
  hasFiltersApplied: boolean;
  onExport: () => void;
}

const ResultSummary = ({
  stats,
  isLoading,
  hasFiltersApplied,
  onExport,
}: ResultSummaryProps) => {
  const { l10n } = useLocalization();

  const emptyState = (
    <ContentCard kind="bordered" paddingSize="xl">
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon}>
          <span className="narmi-icon-download" aria-hidden="true" />
        </div>
        <div className={styles.emptyTextWrapper}>
          <div className={styles.emptyHeading}>
            {l10n.getString("transaction-export-result-empty-heading")}
          </div>
          <div className={styles.emptyHelper}>
            {l10n.getString("transaction-export-result-empty-helper")}
          </div>
        </div>
      </div>
    </ContentCard>
  );

  if (!hasFiltersApplied) {
    return emptyState;
  }

  // ---------- Loading state ----------
  if (isLoading) {
    return (
      <ContentCard kind="bordered" paddingSize="xl">
        <div
          className={styles.loadingWrapper}
          data-testid="result-summary-loading"
        >
          <Spinner size={48} color="var(--color-mediumGrey)" />
        </div>
      </ContentCard>
    );
  }

  // No data once the fetch has settled (e.g. an errored or empty result)
  if (!stats) {
    return emptyState;
  }

  const transactionCount = stats.transaction_count;
  const isZero = transactionCount === 0;

  if (isZero) {
    return (
      <ContentCard kind="bordered" paddingSize="xl">
        <div className={styles.emptyTextWrapper}>
          <div className={styles.emptyHeading}>
            {l10n.getString("transaction-export-result-zero-heading")}
          </div>
          <div className={styles.emptyHelper}>
            {l10n.getString("transaction-export-result-zero-helper")}
          </div>
        </div>
        <hr className={styles.divider} />
        <div className={styles.exportButtonWrapper}>
          <Button
            kind="primary"
            startIcon="download"
            label={l10n.getString("transaction-export-result-export-button")}
            disabled
          />
        </div>
      </ContentCard>
    );
  }

  const netTotalDollars = stats.net_total / 100;
  const creditsCents = Math.abs(stats.credits);
  const debitsCents = Math.abs(stats.debits);
  const creditsDollars = creditsCents / 100;
  const debitsDollars = debitsCents / 100;

  const getNetColorClass = () => {
    if (stats.net_total > 0) return styles.positive;
    if (stats.net_total < 0) return styles.negative;
    return styles.neutral;
  };
  const netColorClass = getNetColorClass();

  const netSignDisplay = stats.net_total > 0 ? "exceptZero" : "auto";

  const moneyBarTotal = creditsCents + debitsCents;
  const moneyBarInPct =
    moneyBarTotal > 0 ? Math.floor((creditsCents / moneyBarTotal) * 100) : 0;
  const moneyBarOutPct = moneyBarTotal > 0 ? 100 - moneyBarInPct : 0;
  const moneyBarAriaLabel = l10n.getString(
    "transaction-export-result-money-bar-label",
    { inPct: moneyBarInPct, outPct: moneyBarOutPct }
  );

  return (
    <ContentCard kind="bordered" paddingSize="xl">
      <div className={styles.header}>
        <div>
          <div className={styles.transactionCount}>{transactionCount}</div>
          <div className={styles.transactionLabel}>
            {l10n.getString("transaction-export-result-transactions-found", {
              count: transactionCount,
            })}
          </div>
        </div>
        <div>
          <div
            className={`${styles.netTotal} ${netColorClass}`}
            data-testid="result-summary-net-total"
          >
            {formatNumber(netTotalDollars, "currency", netSignDisplay)}
          </div>
          <div className={styles.netTotalLabel}>
            {l10n.getString("transaction-export-result-net-total")}
          </div>
        </div>
      </div>

      <div className={styles.moneyBarSection}>
        <MoneyBar
          moneyIn={creditsCents}
          moneyOut={debitsCents}
          ariaLabel={moneyBarAriaLabel}
          testId="money-bar"
        />
        <div className={styles.moneyLegend}>
          <div className={styles.legendItem}>
            <span className={`${styles.legendDot} ${styles.dotIn}`} />
            <span>{l10n.getString("transaction-export-result-money-in")}</span>
            <span className={styles.legendAmount}>
              {formatNumber(creditsDollars, "currency")}
            </span>
          </div>
          <div className={styles.legendItem}>
            <span className={`${styles.legendDot} ${styles.dotOut}`} />
            <span>{l10n.getString("transaction-export-result-money-out")}</span>
            <span className={styles.legendAmount}>
              {formatNumber(debitsDollars, "currency")}
            </span>
          </div>
        </div>
      </div>

      <hr className={styles.divider} />
      <div className={styles.exportButtonWrapper}>
        <Button
          kind="primary"
          startIcon="download"
          label={l10n.getString("transaction-export-result-export-button")}
          onClick={onExport}
        />
      </div>
    </ContentCard>
  );
};

export default ResultSummary;
